import { Location } from '@angular/common';
import { Component, NgZone } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Network } from '@capacitor/network';
import { SplashScreen } from '@capacitor/splash-screen';
import { ProgramStatusEnum } from '@haleo-frontend/data-access/enums';
import { Client, ConsentForm } from '@haleo-frontend/data-access/models';
import {
    AssessmentService,
    AuthService,
    ClientService,
    ConsentFormService,
    NotificationService,
    ProgramService,
    SleepDiaryService
} from '@haleo-frontend/data-access/services';
import { PopoverModal } from '@haleo-frontend/ui';
import { AppService, EnvironmentUtils, LogUtils, TrackingUtils } from '@haleo-frontend/utils';
import { ModalController, Platform } from '@ionic/angular';
import { Storage } from '@ionic/storage';
import { TranslateService } from '@ngx-translate/core';
import * as moment from 'moment';
import { lastValueFrom, Observable, of } from 'rxjs';
import { flatMap, map } from 'rxjs/operators';
import { PaymentCardStatusEnum } from './shared/enums/payment-card-status.enum';
import { ConfigService } from './shared/services/config.service';

import { MessageService } from './shared/services/message.service';

@Component({
    selector: 'app-root',
    templateUrl: 'app.component.html',
    styleUrls: ['app.component.scss']
})
export class AppComponent {

    noInternet: boolean;
    externalCallBack: any;
    client: Client | null;

    constructor(private platform: Platform,
                private notificationService: NotificationService,
                private messageService: MessageService,
                private router: Router,
                private route: ActivatedRoute,
                private location: Location,
                private ngZone: NgZone,
                private storage: Storage,
                private authService: AuthService,
                private consentFormService: ConsentFormService,
                private configService: ConfigService,
                private modalController: ModalController,
                private translateService: TranslateService,
                private assessmentService: AssessmentService,
                private appService: AppService,
                private programService: ProgramService,
                private clientService: ClientService,
                private sleepDiaryService: SleepDiaryService) {
        this.initializeApp();
    }

    initializeApp() {

        this.notificationService.init().subscribe();
        this.platform.ready().then(() => {

            if (this.platform.is('capacitor')) {
                setTimeout(() => SplashScreen.hide(), 1000);
                Network.addListener('networkStatusChange', status => this.noInternet = !status.connected);
                this.migrateStorage();
            }

            LogUtils.init(this.platform.is('ios'),
                this.platform.is('capacitor'),
                this.appService.getAppKey());
            TrackingUtils.init(this.platform, EnvironmentUtils.env.amplitudeKey, EnvironmentUtils.env.firebaseConfig);

            this.checkRequiredActions();
        });

        this.platform.resume
            .pipe(flatMap(() => this.authService.isAuthenticated()),
                flatMap(isAuthenticated => {

                    if (isAuthenticated) {
                        return this.messageService.getList()
                    } else {
                        return of(null);
                    }
                }),
                map(() => this.notificationService.clear()))
            .subscribe(() => this.checkRequiredActions());
    }

    navigateByUrl(url: string) {
        return this.ngZone.run(() => this.router.navigateByUrl(url));
    }

    setStorage(key: string, value: any) {
        return this.ngZone.run(() => this.storage.set(key, value));
    }

    clearStorage() {
        return this.ngZone.run(() => {
            this.storage.clear();
        });
    }

    clearConfig() {
        return this.ngZone.run(() => {
            this.configService.reset();
        });
    }

    private async checkRequiredActions() {

        if (this.location.path().indexOf('screener') < 0) {

            const isAuthenticated = await lastValueFrom(this.authService.isAuthenticated());

            if (isAuthenticated) {

                this.clientService.get().subscribe(client => this.client = client);

                await lastValueFrom(this.checkAppVersion());
                await this.clientService.updateAppVersion();

                if (await lastValueFrom(this.checkTherapySessionSatisfaction())) {
                    return;
                }

                if (await this.displayPopups()) {
                    return;
                }

                const consentFormShown = await lastValueFrom(this.checkConsentForm());

                if (!consentFormShown) {
                    this.checkCreditCard();
                }
            }
        }
    }

    private checkTherapySessionSatisfaction(): Observable<boolean> {

        return this.assessmentService.getSatisfactionTherapySessionToComplete()
            .pipe(map(assessments => {

                if (assessments.length > 0) {

                    this.router.navigate(['/satisfaction/assessment', assessments[0].uuid], {replaceUrl: true});
                    return true;
                }

                return false;
            }));
    }

    private checkConsentForm(): Observable<boolean> {

        return this.consentFormService.getConsentFormsToAccept()
            .pipe(map((consentForms: ConsentForm[]) => consentForms.length > 0));
    }

    private checkCreditCard() {

        this.configService.hasCreditCardExpired().subscribe(creditCardExpired => {

            if (creditCardExpired) {
                this.router.navigate(['/settings/payment', PaymentCardStatusEnum.expired]);
            }
        });

        this.configService.hasLateCancellation().subscribe(lateCancellation => {

            if (lateCancellation) {
                this.router.navigate(['/settings/payment', PaymentCardStatusEnum.lateCancellation]);
            }
        });
    }

    private checkAppVersion(): Observable<void> {

        return this.configService.hasMinimalAppRequiredVersion()
            .pipe(map(has => {

                if (!has) {

                    let modal: any;
                    this.modalController.create({
                        component: PopoverModal,
                        componentProps: {
                            title: 'appUpdateRequired',
                            description: 'appUpdateRequiredDescription',
                            buttons: [{
                                label: 'later',
                                handler: () => modal.dismiss()
                            }, {
                                label: 'update',
                                type: 'solid',
                                handler: () => {

                                    if (this.platform.is('android')) {
                                        window.open(this.translateService.instant('store.android'), '_system');
                                    } else {
                                        window.open(this.translateService.instant('store.ios'), '_system');
                                    }

                                    modal.dismiss();
                                }
                            }]
                        },
                        showBackdrop: true,
                        backdropDismiss: true,
                        cssClass: 'popover-modal small'
                    }).then(m => {
                        modal = m;
                        modal.present();
                    });
                }
            }));
    }

    private async displayPopups() {

        const programs = await lastValueFrom(this.programService.getPrograms());
        const program = programs.length > 0 ? programs[programs.length - 1] : null;

        if (program?.status == ProgramStatusEnum.ongoing && this.client) {

            const sleepQs = await lastValueFrom(this.assessmentService.getSleepQToComplete());
            const consentForms = await lastValueFrom(this.consentFormService.getConsentFormsToAccept());

            if (moment().isAfter(moment(program.startedAt).tz(this.client.timezone).add(36, 'h')) &&
                sleepQs.length > 0 && consentForms.length > 0) {

                await this.router.navigate(['/final-warning']);
                return true;
            }

            const sleepDiary = await lastValueFrom(this.sleepDiaryService.getForDate(moment().format('YYYY-MM-DD')));

            if (sleepQs.length == 0 && !sleepDiary?.completedAt) {

                await this.router.navigate(['sleep-diary-reminder']);
                return true;
            }
        }

        return false;
    }

    /**
     * Migrate LocalStorage to current IonicStorage driver
     * @private
     */
    private async migrateStorage() {

        if (!await this.storage.get('migrated')) {

            const keys = Object.keys(localStorage);

            for (let i = 0; i < keys.length; i++) {

                if (keys[i].indexOf('_ionicstorage') >= 0 && localStorage.getItem(keys[i])) {

                    // Remove key prefix
                    const key = keys[i].replace('_ionicstorage/_ionickv/', '');
                    const value = JSON.parse(localStorage.getItem(keys[i]) || '');
                    this.storage.set(key, value);
                }
            }

            this.storage.set('migrated', true);
        }
    }
}
